/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// redux
import { selectUser } from '../../store/userSlice';
import _ from '../../../@lodash';
import { motion } from 'framer-motion';
// Actions
import { getInstance } from '../../actions';
import { commentCollectionsConfig } from './CommentCollectionsConfig';
// BASE Components
import BASELoading from '../../../@BASE/core/BASELoading';
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
// material-ui
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import TablePagination from '@mui/material/TablePagination';
// comment components
import CommentCard from './CommentCard';
import EmptyCommentsCard from '../cards/snapshots/EmptyCommentsCard';
import authRoles from '../../auth/authRoles';

function CommentList(props) {
  const {
    comment_category,
    simple_list = false,
    instance = null,
    isIMI = false,
    collection = 'comments'
  } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { pathname, search } = location;
  const queryParams = new URLSearchParams(search);
  const pathArray = pathname.split('/');
  const resource = pathArray[1];
  const targetID = pathArray[2];
  const routerParams = useParams();
  
  const [searchText, setSearchText] = useState(
    queryParams.get('searchText') ?? ''
  );
  const [numberOfComments, setNumberOfComments] = useState(
    parseInt(queryParams.get('numberOfComments'), 10) || 10
  );
  const [commentPage, setCommentPage] = useState(
    parseInt(queryParams.get('commentPage'), 10) - 1 || 0
  );
  const [commentInternalVisibility, setCommentInternalVisibility] = useState(
    queryParams.get('commentInternalVisibility') ??
      (user?.internal ? 'both' : 'external')
  );
  const [commentApprovedVisibility, setCommentApprovedVisibility] = useState(
    queryParams.get('commentApprovedVisibility') ?? 'both'
  );
  const [commentReviewVisibility, setCommentReviewVisibility] = useState(
    queryParams.get('commentReviewVisibility') ?? 'all'
  );
  //   const [commentInternalExport, setCommentInternalExport] = useState(
  //     queryParams.get('commentInternalExport') ??
  //       (user?.internal ? 'both' : 'external')
  //   );
  const [commentInternalExport, setCommentInternalExport] =
    useState('external');
  const [commentApprovedExport, setCommentApprovedExport] = useState(
    queryParams.get('commentApprovedExport') ?? 'both'
  );
  const [commentReviewExport, setCommentReviewExport] = useState(
    queryParams.get('commentReviewExport') ?? 'all'
  );
  const [searchInput, setSearchInput] = useState(searchText);
  const [isSearchEnabled, setIsSearchEnabled] = useState(
    searchText.length >= 3
  );
  const [comments, setComments] = useState([]);
  const [groupedComments, setGroupedComments] = useState(null);
  const [totalCommentCount, setTotalCommentCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);

  const {
    fetchComments,
    updateComments,
    commentCategory,
    color
  } = commentCollectionsConfig[collection];

  const prevFiltersRef = useRef({
    pathname,
    numberOfComments,
    searchText,
    commentInternalVisibility,
    commentApprovedVisibility,
    commentReviewVisibility
  });

  const updateURLParams = (keyOrObject, value) => {
    const params = new URLSearchParams(search);

    if (typeof keyOrObject === 'object') {
      // Multiple updates
      Object.entries(keyOrObject).forEach(([key, val]) => {
        if (val === '' || val === null) {
          params.delete(key);
        } else {
          params.set(key, val);
        }
      });
    } else {
      // Single update
      if (value === '' || value === null) {
        params.delete(keyOrObject);
      } else {
        params.set(keyOrObject, value);
      }
    }

    navigate(`${window.location.pathname}?${params.toString()}`, {
      replace: true
    });
  };

  // get comments from api
  const getComments = async () => {
    try {
      const getReviews = async () => {
        if (instance?.reviews) {
          return instance.reviews; // If reviews exist at the root level
        } else if (instance?.lesson?.reviews) {
          return instance.lesson.reviews; // If reviews exist in lesson
        } else if (instance?.section?.lesson?.reviews) {
          try {
            const response = await dispatch(
              getInstance('sections')(instance?.section?.id)
            ).unwrap();

            return response?.lesson?.reviews || [];
          } catch (err) {
            console.log(err);
            return [];
          }
        }
        return []; // Default to an empty array if no reviews found
      };

      const reviews = await getReviews();

      const reviewPeriods =
        commentReviewVisibility !== 'all'
          ? reviews
              .filter((review) =>
                review.cycle.startsWith(commentReviewVisibility)
              )
              .map((review) => review.id)
          : [];

      const response = await dispatch(
        fetchComments(comment_category)({
          id: routerParams.id,
          numberOfComments: numberOfComments,
          commentPage: commentPage + 1, // Convert back to 1-based for backend
          searchText: searchText,
          showApproved: commentApprovedVisibility,
          showInternal: commentInternalVisibility,
          ...(reviewPeriods ? { reviewPeriods } : {})
        })
      ).unwrap();
      setComments(response.comments);
      setGroupedComments(groupComments(response.comments));
      setTotalCommentCount(response.total_count);
    } catch (err) {
      console.log(err);
    }
  };

  const groupComments = (comments) => {
    const organizeComments = (comments) => {
      const organized = [];

      comments.forEach((comment) => {
        const courseId = comment.course.id;
        const lessonId = comment.lesson?.id;
        const sectionId = comment.section?.id;
        const pageId = comment.page?.id;
        const scope = comment.scope;

        // Find or create the course
        let course = organized.find((c) => c.id === courseId);
        if (!course) {
          course = { ...comment.course, lessons: [], comments: [] };
          organized.push(course);
        }

        // Add comment to course level if scope is 'course'
        if (scope === 'course') {
          course.comments.push(comment);
          return;
        }

        // Find or create the lesson
        if (lessonId) {
          let lesson = course.lessons.find((l) => l.id === lessonId);
          if (!lesson) {
            lesson = { ...comment.lesson, sections: [], comments: [] };
            course.lessons.push(lesson);
          }

          // Add comment to lesson level if scope is 'lesson'
          if (scope === 'lesson') {
            lesson.comments.push(comment);
            return;
          }

          // Find or create the section
          if (sectionId) {
            let section = lesson.sections.find((s) => s.id === sectionId);
            if (!section) {
              section = { ...comment.section, pages: [], comments: [] };
              lesson.sections.push(section);
            }

            // Add comment to section level if scope is 'section'
            if (scope === 'section') {
              section.comments.push(comment);
              return;
            }

            // Handle pages if needed (not shown in data but can be extended here)
            if (pageId) {
              let page = section.pages.find((p) => p.id === pageId);
              if (!page) {
                page = { ...comment.page, comments: [] };
                section.pages.push(page);
              }

              // Add comment to page level if scope is 'page'
              if (scope === 'page') {
                page.comments.push(comment);
                return;
              }
            }
          }
        }
      });

      return organized;
    };

    const organizedComments = organizeComments(comments);
    return organizedComments;
  };

  useEffect(() => {
    const prev = prevFiltersRef.current;

    const pathnameChanged = simple_list ? prev.pathname !== pathname : false;

    const filterChanged =
      pathnameChanged ||
      prev.numberOfComments !== numberOfComments ||
      prev.searchText !== searchText ||
      prev.commentInternalVisibility !== commentInternalVisibility ||
      prev.commentApprovedVisibility !== commentApprovedVisibility ||
      prev.commentReviewVisibility !== commentReviewVisibility;

    if (filterChanged && commentPage !== 0) {
      // Reset page if any filter changed
      setCommentPage(0);
      updateURLParams('commentPage', 1); // Keep URL in sync (1-based)
      return; // Exit early so getComments uses correct page
    }

    // Always run this if page didn't change or page already 0
    getComments();

    // Store current filters for next time
    prevFiltersRef.current = {
      pathname,
      numberOfComments,
      searchText,
      commentInternalVisibility,
      commentApprovedVisibility,
      commentReviewVisibility
    };
  }, [
    pathname,
    numberOfComments,
    commentPage,
    searchText,
    commentInternalVisibility,
    commentApprovedVisibility,
    commentReviewVisibility
  ]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchText('');
    updateURLParams('searchText', '');
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    setIsSearchEnabled(value.length >= 3);
  };

  const handleSearchSubmit = () => {
    updateURLParams('searchText', searchInput);
    setSearchText(searchInput);
  };

  const handlePageChange = (event, newPage) => {
    updateURLParams('commentPage', newPage + 1); // Store as 1-based in URL
    setCommentPage(newPage); // Keep state 0-based for TablePagination
  };

  const handleFilterToggle = () => {
    if (exportsOpen) setExportsOpen(false);
    setFiltersOpen((prev) => !prev);
  };

  const handleExportToggle = () => {
    if (filtersOpen) setFiltersOpen(false);
    setExportsOpen((prev) => !prev);
  };

  const handleInternalVisibilityChange = (event) => {
    const value = event.target.value;
    updateURLParams('commentInternalVisibility', value);
    setCommentInternalVisibility(value);
  };

  const handleApprovedVisibilityChange = (event) => {
    const value = event.target.value;
    updateURLParams('commentApprovedVisibility', value);
    setCommentApprovedVisibility(value);
  };

  const handleReviewVisibilityChange = (event) => {
    const value = event.target.value;
    updateURLParams('commentReviewVisibility', value);
    setCommentReviewVisibility(value);
  };

  const handleInternalExportChange = (event) => {
    const value = event.target.value;
    updateURLParams('commentInternalExport', value);
    setCommentInternalExport(value);
  };

  const handleApprovedExportChange = (event) => {
    const value = event.target.value;
    updateURLParams('commentApprovedExport', value);
    setCommentApprovedExport(value);
  };

  const handleReviewExportChange = (event) => {
    const value = event.target.value;
    updateURLParams('commentReviewExport', value);
    setCommentReviewExport(value);
  };

  const handleApproveAll = () => {
    let comment_ids = [];
    for (let i = 0; i < comments.length; i++) {
      comment_ids.push(comments[i].id);
    }

    let data = {
      user: user._id || user.id,
      ids: comment_ids
    };

    dispatch(updateComments(commentCategory)(data)).then(({ payload }) => {
      getComments();
    });
  };

  const flattenComments = (comments, parentId = null, level = 0) => {
    let flattened = [];

    comments.forEach((comment) => {
      flattened.push({
        'Page ID': comment.page?.id || 'Reply to comment',
        'Comment ID': comment.id,
        'Parent ID': parentId, // Track the parent comment
        'Nesting Level': level, // Indicate nesting level
        Title: comment.title || 'No Title',
        Content: comment.content || 'No Content',
        Creator: comment.creator?.first_name + ' ' + comment.creator?.last_name,
        'Created Date': new Date(comment.created_date).toLocaleString(),
        Approved: comment.approved ? 'Yes' : 'No',
        'Replies Count': comment.replies?.length || 0,
        'View Page': comment.page?.id
          ? `${window.location.origin}/pages/${comment.page.id}`
          : ''
      });

      // Recursively add replies
      if (comment.replies && comment.replies.length > 0) {
        flattened = flattened.concat(
          flattenComments(comment.replies, comment.id, level + 1)
        );
      }
    });

    return flattened;
  };

  const getExportComments = async () => {
    try {
      const reviewPeriods =
        commentReviewExport !== 'all'
          ? instance.reviews
              .filter((review) => review.cycle.startsWith(commentReviewExport))
              .map((review) => review.id)
          : [];

      const response = await dispatch(
        fetchComments(comment_category)({
          id: routerParams.id,
          showApproved: commentApprovedExport,
          showInternal: commentInternalExport,
          ...(reviewPeriods ? { reviewPeriods } : {}) // Only include if it's not undefined
        })
      ).unwrap();

      return response.comments;
    } catch (err) {
      console.error('Error fetching export comments:', err);
      return [];
    }
  };

  const exportToExcel = async () => {
    const exportComments = await getExportComments();

    if (!exportComments || exportComments.length === 0) {
      // this needs to be a toast or something
      console.log('No comments available to export.');
      return;
    }

    const data = flattenComments(exportComments);

    const worksheet = XLSX.utils.json_to_sheet(data);

    // Apply column headers explicitly to ensure spaces are preserved
    const columnHeaders = {
      A1: { v: 'Page ID' },
      B1: { v: 'Comment ID' },
      C1: { v: 'Parent ID' },
      D1: { v: 'Nesting Level' },
      E1: { v: 'Title' },
      F1: { v: 'Content' },
      G1: { v: 'Creator' },
      H1: { v: 'Created Date' },
      I1: { v: 'Approved' },
      J1: { v: 'Replies Count' },
      K1: { v: 'View Page' }
    };

    Object.assign(worksheet, columnHeaders);

    // Find column index for "View Page"
    const columnIndex = Object.keys(data[0]).indexOf('View Page');

    // Insert actual Excel formulas into the worksheet without changing `data`
    data.forEach((row, rowIndex) => {
      if (row['View Page']) {
        const cellAddress = XLSX.utils.encode_cell({
          r: rowIndex + 1,
          c: columnIndex
        }); // +1 for headers
        worksheet[cellAddress] = {
          t: 's', // Type: string
          f: `HYPERLINK("${row['View Page']}", "View Page")`
        };
      }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comments');

    // Convert to Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(dataBlob, 'comments.xlsx');
  };

  if (!comments) {
    return <BASELoading />;
  }

  const renderOrganizedComments = (items, level = 0, levelLabel) => {
    if (!items || items.length === 0) return null;

    const getProdPageLink = (label, id) => {
      const splitLabel = label.split('-');
      const lang = splitLabel[0].toLowerCase();
      const module = splitLabel.length > 3 ? splitLabel[1].toLowerCase() + '-' + splitLabel[2].toLowerCase() : splitLabel[1].toLowerCase();

      return `https://p-0033-${lang}.web.app/${module}/#/${id}`;
    };

    const getBackgroundColor = (index, level, levelLabel) => {
      const colors = [
        ['#111827', '#111827'], // Bluish greys for courses
        ['#111827', '#111827'], // Greenish greys for lessons
        ['#111827', '#111827'], // Darker greys for sections
        ['#111827', '#111827'] // Light greys for pages
      ];
      const levelColors = {
        courses: '#111827',
        lessons: '#111827',
        sections: '#111827',
        pages: color
      };
      // return colors[level % colors.length][index % 2]; // Alternate between two colors
      return levelColors[levelLabel];
    };

    const textColor = collection !== 'pre_trial_comments' ? '#ffffff' : levelLabel === 'pages' ? '#000000' : '#ffffff';

    return (
      <motion.div
        variants={{
          show: {
            transition: { staggerChildren: 0.04 }
          }
        }}
        initial="hidden"
        animate="show"
      >
        {items.map((item, index) => (
          

          <motion.div
            key={item.id}
            variants={{
              hidden: { opacity: 0, y: 10 },
              show: { opacity: 1, y: 0 }
            }}
          >
            <Accordion defaultExpanded className="border-1">
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ fill: textColor }} />
                }
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  backgroundColor: getBackgroundColor(index, level, levelLabel),
                  color: textColor,
                  minHeight: '56px !important',
                  '& .MuiAccordionSummary-content': {
                    margin: '0px !important'
                  }
                }}
              >
                {/* Display course/lesson/section/page labels */}
                <div className="flex grow w-full">
                  <List disablePadding className="grow">
                    <ListItem disablePadding>
                      <ListItemIcon className="align-center text-white">
                        <BASESvgIcon size={24} sx={{ color: textColor }}>
                          heroicons-outline:folder
                        </BASESvgIcon>
                      </ListItemIcon>
                      <ListItemText
                        primary={`${
                          levelLabel.charAt(0).toUpperCase() +
                          levelLabel.slice(1, -1)
                        }: ${item.title} Comments`}
                        secondary={item.label}
                        // slotProps={{ secondary: { className: "text-white" } }}
                        secondaryTypographyProps={{ color: textColor }}
                      />
                    </ListItem>
                  </List>
                  <Tooltip
                    title={`View ${
                      levelLabel.slice(0, -1).charAt(0).toUpperCase() +
                      levelLabel.slice(1, -1)
                    }`}
                  >
                    <IconButton
                      aria-label={`View ${
                        levelLabel.slice(0, -1).charAt(0).toUpperCase() +
                        levelLabel.slice(1, -1)
                      }`}
                      className="mx-16 text-white"
                      onClick={() => navigate(`/${levelLabel}/${item.id}`)}
                    >
                      <BASESvgIcon sx={{ color: textColor }}>heroicons-outline:external-link</BASESvgIcon>
                    </IconButton>
                  </Tooltip>
                  {(levelLabel === 'pages' && collection !== 'comments') && (
                      <Tooltip
                        title={`View ${
                          levelLabel.slice(0, -1).charAt(0).toUpperCase() +
                          levelLabel.slice(1, -1)
                        }`}
                      >
                        <IconButton
                          aria-label="View IMI Page"
                          className="mr-16"
                          href={getProdPageLink(item.label, item.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <BASESvgIcon sx={{ color: textColor }}>heroicons-outline:desktop-computer</BASESvgIcon>
                        </IconButton>
                      </Tooltip>
                  )}
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {/* Render comments for the current level */}
                {item.comments && item.comments.length > 0 ? (
                  <motion.div
                    variants={{
                      show: { transition: { staggerChildren: 0.04 } }
                    }}
                  >
                    {item.comments.map((comment) => (
                      <CommentCard
                        key={comment.id}
                        comment={comment}
                        user={user}
                        searchText={searchText}
                        isIMI={isIMI}
                        collection={collection}
                        resource={resource}
                      />
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    variants={{
                      show: { transition: { staggerChildren: 0.04 } }
                    }}
                  >
                    <EmptyCommentsCard
                      resource={levelLabel.slice(0, -1)}
                      global
                    />
                  </motion.div>
                )}

                {/* Recursively render child levels */}
                {item.lessons &&
                  renderOrganizedComments(item.lessons, level + 1, 'lessons')}
                {item.sections &&
                  renderOrganizedComments(item.sections, level + 1, 'sections')}
                {item.pages &&
                  renderOrganizedComments(item.pages, level + 1, 'pages')}
              </AccordionDetails>
            </Accordion>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  function findObjectById(resource, targetId) {
    // Check if the resource is an array
    if (Array.isArray(resource)) {
      for (const item of resource) {
        const result = findObjectById(item, targetId);
        if (result) {
          return result;
        }
      }
    }
    // Check if the resource is an object
    else if (typeof resource === 'object' && resource !== null) {
      if (resource.id === targetId) {
        return resource; // Found the target object
      }
      for (const key in resource) {
        if (Array.isArray(resource[key]) || typeof resource[key] === 'object') {
          const result = findObjectById(resource[key], targetId);
          if (result) {
            return result;
          }
        }
      }
    }
    return null; // Return null if the target is not found
  }

  const displayOrganizedComments = () => {
    if (!groupedComments) return;

    const foundTarget = [findObjectById(groupedComments, targetID)];

    return renderOrganizedComments(foundTarget, 0, resource);
  };

  const renderSimpleList = () => {
    if (comments && comments.length > 0) {
      return (
        <motion.div
          variants={{
            show: {
              transition: {
                staggerChildren: 0.04
              }
            }
          }}
          initial="hidden"
          animate="show"
        >
          {comments.map((comment) => {
            return (
              <motion.div
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 10
                  },
                  show: {
                    opacity: 1,
                    y: 0
                  }
                }}
                key={comment.id}
              >
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  user={user}
                  searchText={searchText}
                  isIMI={isIMI}
                  collection={collection}
                  resource={resource}
                />
              </motion.div>
            );
          })}
        </motion.div>
      );
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full mx-auto px-0 pb-32">
      <div
        className={`flex shrink-0 items-center justify-between space-y-16 sm:space-y-0 ${
          simple_list ? 'flex-col' : 'flex-row'
        }`}
      >
        <Paper
          component="form"
          sx={{
            p: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            width: 352,
            height: 64
          }}
          elevation={0}
          square
          className="border-b-1 mx-16 bg-transparent"
        >
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search Comments"
            inputProps={{ 'aria-label': 'search comments' }}
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault(); // Prevent form submission/reload
                handleSearchSubmit();
              }
            }}
            className="grow"
          />
          {searchInput && (
            <IconButton
              type="button"
              sx={{ p: '10px' }}
              aria-label="clear search"
              onClick={handleClearSearch}
            >
              <BASESvgIcon>heroicons-outline:x</BASESvgIcon>
            </IconButton>
          )}
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <IconButton
            type="button"
            sx={{ p: '10px' }}
            aria-label="search"
            onClick={handleSearchSubmit}
            disabled={!isSearchEnabled}
          >
            <BASESvgIcon>heroicons-outline:search</BASESvgIcon>
          </IconButton>
        </Paper>
        {comment_category !== 'courses' && (
          <div
            className={`flex w-full items-center p-16 ${
              simple_list ? 'justify-between' : 'justify-end space-x-16'
            }`}
          >
            <Button
              variant="outlined"
              size="small"
              aria-label="Filter"
              startIcon={
                <BASESvgIcon size={16}>
                  heroicons-outline:adjustments
                </BASESvgIcon>
              }
              onClick={handleFilterToggle}
            >
              Filter
            </Button>
            {comment_category === 'lessons' &&
              authRoles.leadEditor.includes(user.role) && (
                <Button
                  variant="outlined"
                  size="small"
                  aria-label="Filter"
                  startIcon={
                    <BASESvgIcon size={16}>
                      heroicons-solid:download
                    </BASESvgIcon>
                  }
                  onClick={handleExportToggle}
                >
                  Excel
                </Button>
              )}
            <Button
              variant="contained"
              color="success"
              size="small"
              aria-label="Approve"
              startIcon={
                <BASESvgIcon size={16}>heroicons-outline:check</BASESvgIcon>
              }
              onClick={() => handleApproveAll()}
            >
              Approve All
            </Button>
          </div>
        )}
      </div>
      <motion.div
        initial={false}
        animate={{
          height: filtersOpen ? 'auto' : 0,
          opacity: filtersOpen ? 1 : 0
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
        className="w-full pb-16"
      >
        <Typography className="text-16 font-semibold grow p-16">
          Filters
        </Typography>
        <div
          className={`relative w-full flex justify-between p-16 ${
            simple_list ? 'flex-col space-y-16' : 'flex-row space-x-16'
          }`}
        >
          {user?.internal && (
            <FormControl variant="standard" className="w-full">
              <InputLabel id="comment-internal-visibility-label">
                <div className="flex items-center">
                  <BASESvgIcon>heroicons-outline:eye</BASESvgIcon>
                  <span className="ml-8">Published Visibility</span>
                </div>
              </InputLabel>
              <Select
                labelId="comment-internal-visibility-label"
                value={commentInternalVisibility}
                onChange={handleInternalVisibilityChange}
              >
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl variant="standard" className="w-full">
            <InputLabel id="comment-approved-visibility-label">
              <div className="flex items-center">
                <BASESvgIcon color="success">
                  heroicons-solid:check-circle
                </BASESvgIcon>
                <span className="ml-8">Approved Visibility</span>
              </div>
            </InputLabel>
            <Select
              labelId="comment-approved-visibility-label"
              value={commentApprovedVisibility}
              onChange={handleApprovedVisibilityChange}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard" className="w-full">
            <InputLabel id="comment-review-visibility-label">
              <div className="flex items-center">
                <BASESvgIcon>heroicons-outline:calendar</BASESvgIcon>
                <span className="ml-8">Review Visibility</span>
              </div>
            </InputLabel>
            <Select
              labelId="comment-review-visibility-label"
              value={commentReviewVisibility}
              onChange={handleReviewVisibilityChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="dev_alpha">SB Alpha</MenuItem>
              <MenuItem value="dev_beta">SB Beta</MenuItem>
              <MenuItem value="dev_final">SB Final</MenuItem>
            </Select>
          </FormControl>
        </div>
      </motion.div>
      <motion.div
        initial={false}
        animate={{
          height: exportsOpen ? 'auto' : 0,
          opacity: exportsOpen ? 1 : 0
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
        className="w-full pb-16"
      >
        <Typography className="text-16 font-semibold grow p-16">
          Export Filters
        </Typography>
        <div
          className={`relative w-full flex justify-between p-16 ${
            simple_list ? 'flex-col space-y-16' : 'flex-row space-x-16'
          }`}
        >
          <Button
            variant="contained"
            color="success"
            size="small"
            aria-label="Approve"
            startIcon={
              <BASESvgIcon size={16}>heroicons-outline:check</BASESvgIcon>
            }
            onClick={() => exportToExcel()}
          >
            Export
          </Button>
          <FormControl variant="standard" className="w-full">
            <InputLabel id="comment-internal-export-label">
              <div className="flex items-center">
                <BASESvgIcon>heroicons-outline:eye</BASESvgIcon>
                <span className="ml-8">Export Published</span>
              </div>
            </InputLabel>
            <Select
              labelId="comment-internal-export-label"
              value={commentInternalExport}
              onChange={handleInternalExportChange}
            >
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard" className="w-full">
            <InputLabel id="comment-approved-export-label">
              <div className="flex items-center">
                <BASESvgIcon color="success">
                  heroicons-solid:check-circle
                </BASESvgIcon>
                <span className="ml-8">Export Approved</span>
              </div>
            </InputLabel>
            <Select
              labelId="comment-approved-export-label"
              value={commentApprovedExport}
              onChange={handleApprovedExportChange}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="standard" className="w-full">
            <InputLabel id="comment-review-export-label">
              <div className="flex items-center">
                <BASESvgIcon>heroicons-outline:calendar</BASESvgIcon>
                <span className="ml-8">Export Review</span>
              </div>
            </InputLabel>
            <Select
              labelId="comment-review-export-label"
              value={commentReviewExport}
              onChange={handleReviewExportChange}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="dev_alpha">SB Alpha</MenuItem>
              <MenuItem value="dev_beta">SB Beta</MenuItem>
              <MenuItem value="dev_final">SB Final</MenuItem>
            </Select>
          </FormControl>
        </div>
      </motion.div>
      {!comments || comments.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <Typography color="text.secondary" className="text-xl my-24">
            No comments found!
          </Typography>
        </div>
      ) : simple_list ? (
        renderSimpleList()
      ) : (
        displayOrganizedComments()
      )}
      <div className="flex items-center justify-center">
        <TablePagination
          component="div"
          count={totalCommentCount}
          page={commentPage} // TablePagination uses 0-based index
          rowsPerPage={numberOfComments}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            updateURLParams({
              numberOfComments: newRowsPerPage,
              commentPage: 1
            });
            setNumberOfComments(newRowsPerPage);
            setCommentPage(0); // Reset to first page
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </div>
    </div>
  );
}

export default CommentList;
